import React, { CSSProperties, FC, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { ImageRenderer } from '@src/atoms/ImageRenderer';
import { Skeleton } from '@src/atoms/Skeleton';
import { AnyType } from '@src/models/common';
import { IWithStylesProps } from '@src/models/props';
import { COEFFICIENT } from '../const';
import { Text } from '@src/atoms/Text';
import { Box } from '@src/atoms/Box';
import styles from './SliderItem.module.scss';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { classnames } from '@src/utils/classnames';

interface IImg {
  /** Уникальный идентификатор файла */
  id?: string;
  /** Урл большого изображения */
  url: string;
  /** Урл загруженного изображения */
  mainUrl?: string;
  /** Урл уменьшенного загруженного изображения */
  previewUrl?: string;
  /** Урл уменьшенного изображения */
  thumb?: string;
  /** Подпись изображения в случае пробем с загрузкой по url */
  alt?: string;
  /** название файла */
  originalName?: string;

  /** Описание под слайдом */
  description?: string;
}

interface ISliderProps extends IWithStylesProps {
  img: IImg;
  /**
   * Коэффициент отношения ширины и высоты у изображения.
   * Чаще всего у нас постоянные и известные отношения для изображений. Учитывая что ширина будет всегда = ширине родительского контейнера, за счет этого коэффициента можно высчитать высоту для формировния скелетона и общего размера контейнера.
   * Это позволяет избежать мерцания контейнера как при первоначальном рендере так и при переключении слайдеров, а так же корректно отобразить скелетон при загрузке изображения.
   */
  coeff?: number;
  height: number | string;
  changeHeight?: (n: number) => void;
  onError?: OnErrorEventHandler;
  onLoaded?: (args: AnyType) => void;
  handleZoom?: () => void;
  isInZoom?: boolean;
  /** Показывает, что хотя бы один слайд с описанием, нужно чтобы слайды не прыгали */
  hasOneDescription?: boolean;
}

/** Компонент выполняет предзагрузку изображение, а также показывает skeleton в процессе загрузки или неудачи */
export const SliderItem: FC<ISliderProps> = ({
  img,
  coeff = COEFFICIENT,
  height,
  className,
  style,
  changeHeight,
  onError,
  onLoaded,
  handleZoom,
  isInZoom,
}) => {
  const ref = useRef<HTMLImageElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [isImage, setIsImage] = useState(true);
  const { isMobile } = useContext(BreakpointsContext);

  useEffect(() => {
    if (!ref.current) {
      ref.current = new Image();
    }
    //TO-DO добавить поле типа файла в апишке
    if (img.previewUrl?.includes('cdn.centra.ai/app-static/o/mime_icon/')) {
      setIsImage(false);
    }

    if (img.thumb && img.url && img.mainUrl) {
      ref.current.src = img.thumb || img.url || img.mainUrl;
    }

    ref.current.onload = (...args) => {
      setLoaded(true);
      onLoaded?.(...args);
    };
    ref.current.onerror = (...args) => {
      onError?.(...args);
    };
  }, [img.thumb, img.url, img.mainUrl, onError, onLoaded, img.previewUrl, img.originalName]);

  const itemStyle = useMemo<CSSProperties>(
    () => ({
      height,
      ...style,
      width: '100%',
    }),
    [height, style],
  );

  return (
    <div
      key={img.id}
      ref={(ref: HTMLDivElement) => {
        if (ref && !height) {
          const width = ref.getBoundingClientRect().width;
          changeHeight?.(width / coeff);
        }
      }}
      className={className}
      style={itemStyle}
      onClick={handleZoom}
    >
      {loaded ? (
        <Skeleton.Rect height={height} />
      ) : isImage ? (
        <>
          <ImageRenderer
            url={img.mainUrl ? img.mainUrl : img.url}
            thumb={img.thumb}
            alt={img.alt}
            fit={isInZoom ? 'contain' : undefined}
            uploadImmediately
          />
          {img.description && (
            <Text
              id={'fileDescriptionText'}
              noOfLines={3}
              size="secondary"
              color="basicGreySemiDark"
              className={classnames(styles.description, isMobile && styles.descriptionMobile)}
            >
              {img.description}
            </Text>
          )}
        </>
      ) : (
        img.previewUrl && (
          <Box justifyContent={'center'} alignItems={'center'} className={styles.noPreviewContainer} column>
            <Box className={styles.iconContainer}>
              <ImageRenderer
                url={img.previewUrl && img.previewUrl}
                thumb={img.previewUrl}
                alt={img.alt}
                uploadImmediately
              />
            </Box>
            <Text id={'noPreviewText'} color={'basicWhite'} weight={'bold'}>
              Превью файла недоступно
            </Text>
            <Text id={'fileOriginalNameNoPreviewText'} color={'basicWhite'}>
              {img.originalName}
            </Text>
          </Box>
        )
      )}
    </div>
  );
};
